// src/components/NotificationDialog.js
import React, { useCallback, useEffect, useState } from "react";
import { Dialog } from "@headlessui/react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import API_URL from "../config";
import { Bell, Send, CheckCircle, AlertCircle, ClipboardList, CircleAlert } from "lucide-react";
import { useLanguage } from '../context/LanguageContext';
import { formatDateTime24 } from '../utils/dateTimeFormat';
import { getLocalDevNotifications, isLocalDevCompanyId } from "../utils/localDevAuth";
import { getApiErrorMessage, getAuthHeaders, getStoredAccessToken, getStoredUser } from "../utils/authSession";

const NotificationDialog = ({ open, onClose }) => {
  const { t, locale } = useLanguage();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const user = getStoredUser();
  const notificationMessage = (notification) =>
    locale?.startsWith("hi") && notification?.message_hi
      ? notification.message_hi
      : notification?.message;

  const typeIcons = {
    task_submission: <Send className="mr-2 h-4 w-4 text-blue-600" />,
    task_pending_approval: <Send className="mr-2 h-4 w-4 text-blue-600" />,
    task_approved: <CheckCircle className="mr-2 h-4 w-4 text-green-500" />,
    task_declined: <AlertCircle className="mr-2 h-4 w-4 text-red-500" />,
    task_assignment: <ClipboardList className="mr-2 h-4 w-4 text-amber-500" />,
    deadline_reminder: <AlertCircle className="mr-2 h-4 w-4 text-amber-500" />,
    approval_delay: <CircleAlert className="mr-2 h-4 w-4 text-purple-500" />,
    default: <CircleAlert className="mr-2 h-4 w-4 text-muted" />,
  };

  const getNotificationTarget = useCallback((item) => {
    const type = String(item?.notification?.type || '').toLowerCase();
    const role = String(user?.role || '').toLowerCase();

    if (role === 'admin') {
      return '/admin';
    }

    if (role === 'manager') {
      if (['task_submission', 'task_pending_approval'].includes(type)) {
        return '/manager';
      }
      if (['task_assignment', 'task_approved', 'task_declined', 'deadline_reminder', 'approval_delay'].includes(type)) {
        return '/manager/my-tasks';
      }
      return '/manager';
    }

    if (role === 'team_member') {
      if (['task_assignment', 'task_pending_approval', 'task_approved', 'task_declined', 'deadline_reminder'].includes(type)) {
        return '/team-member/my-tasks';
      }
      return '/team-member';
    }

    return null;
  }, [user?.role]);

  const handleNotificationClick = useCallback((item) => {
    const target = getNotificationTarget(item);
    onClose?.();
    if (target) {
      navigate(target);
    }
  }, [getNotificationTarget, navigate, onClose]);



  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      setError('');
      if (isLocalDevCompanyId(user?.company?.id) || isLocalDevCompanyId(localStorage.getItem("company_id"))) {
        setNotifications(getLocalDevNotifications(user?.email));
        return;
      }

      const token = getStoredAccessToken();
      if (!token || !user?.email) {
        setNotifications([]);
        return;
      }

      const res = await axios.get(`${API_URL}/api/notifications/`, {
        params: { email: user.email },
        headers: getAuthHeaders(token),
      });
      setNotifications(res.data || []);
    } catch (err) {
      setNotifications([]);
      setError(getApiErrorMessage(err, {
        fallback: t('notifications.noNotificationsFound'),
      }));
    } finally {
      setLoading(false);
    }
  }, [t, user?.company?.id, user?.email]);
  useEffect(() => {
    if (open) {
      fetchNotifications();
    }
  }, [fetchNotifications, open]);

  useEffect(() => {
    const handleRefresh = () => {
      if (open) {
        fetchNotifications();
      }
    };
    window.addEventListener('notifications:refresh', handleRefresh);
    return () => window.removeEventListener('notifications:refresh', handleRefresh);
  }, [fetchNotifications, open]);

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <div className="overlay-backdrop fixed inset-0" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center">
        <div className="dialog-surface w-full max-w-lg p-6">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Bell className="h-5 w-5 text-purple-500" />
            {t('notifications.allNotifications')}
          </h3>

          <div className="mt-4 max-h-80 overflow-y-auto space-y-3">
            {loading ? (
              <p className="text-sm text-secondary-text">{t('common.loading')}</p>
            ) : error ? (
              <p className="text-sm text-secondary-text">{error}</p>
            ) : notifications.length > 0 ? (
              notifications.map((n, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => handleNotificationClick(n)}
                  className="flex w-full items-start rounded-2xl border border-border p-3 text-left transition duration-200 hover:bg-purple-300/10"
                >
                  <div className="mt-1">
                    {typeIcons[n.notification.type] || typeIcons.default}
                  </div>
                  <div>
                    <p className="text-sm text-text">{notificationMessage(n.notification)}</p>
                    <p className="text-xs text-secondary-text">
                      {formatDateTime24(n.notification.created_at, locale)}
                    </p>
                  </div>
                </button>
              ))
            ) : (
              <p className="text-sm text-secondary-text">{t('notifications.noNotificationsFound')}</p>
            )}
          </div>

          <div className="mt-6 flex justify-end">
            <button
              className="primary-btn px-4 py-2"
              onClick={onClose}
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default NotificationDialog;
